import type { CalendarEvent, CalendarParticipant } from '@wtfalch/calendar/calendar';
import type { ChatClient, ChatRoomSummary } from '@wtfalch/chat';
import type { IconName } from '@wtfalch/design';
import type { ThreadSummary } from '@wtfalch/email/mailbox';
import { durationMinutes, eventTimeLabel } from '../calendar/layout.ts';
import { eventCallPath, hasOwnEventCall } from '../calls/eventCalls.ts';
/**
 * Turning each SDK's own signals into one row shape for "Waiting for you",
 * and the day's events into `Agenda` items for "Your day" -- pure, so the
 * mapping and the ordering can be tested without a client, a DOM or a
 * network (`rows.test.ts`). `Today.tsx` owns the fetching; this file owns
 * the arithmetic.
 *
 * There is no Activity feed service (agora#215): each source is read
 * straight off its own SDK, and there is no signal in any of the three for
 * "urgent" -- `MailClient` carries no priority, a calendar invite carries no
 * importance, and `mentionsMe` is binary. Every row below is `urgent:
 * false`, kept as a real field (not dropped) so "urgent first, then newest"
 * still has something to sort on if a source ever grows one.
 */
import type { ChatPresence } from '../chat/types.ts';
import { displayName, formatWhen } from '../mail/format.ts';
import { pathForCalendar, pathForChat, pathForMail } from '../routes.ts';

/** `@wtfalch/chat` keeps `ChatTimelineEvent` behind its own surface --
 *  `ChatClient.timeline()`'s return type is the only place it is public --
 *  so this derives the same shape from `ChatClient` itself rather than
 *  naming a type the package does not export. */
export type ChatTimelineEvent = ReturnType<ChatClient['timeline']>['events'][number];

export type WaitingRow = {
  id: string;
  source: 'mail' | 'calendar' | 'chat';
  icon: IconName;
  who: string;
  what: string;
  detail: string;
  /** ISO instant, for ordering and for `formatWhen`. */
  whenIso: string;
  urgent: boolean;
  action: { label: string; path: string };
  /** Set on an invitation still needing an answer, so the row can say yes or
   *  no in place. */
  invite?: { eventId: string; title: string };
};

/** A row's `detail` line, capped with its own ellipsis rather than left to
 *  CSS alone. A chat message has no length limit at all, and
 *  `@wtfalch/email/mailbox/fake`'s own preview is already cut at a fixed
 *  80 characters with no ellipsis of its own -- on a "Waiting for you"
 *  column wide enough to match the board's own proportions, that 80-char
 *  string can fit with room to spare, so nothing overflows and CSS never
 *  gets the chance to add the "…" that would say more was cut, and the
 *  line reads as cut off mid-word instead. 72 is short enough to undercut
 *  that (and a real server's own preview length, typically longer still),
 *  so a row's detail says so itself whenever there was more, regardless of
 *  the column's width or a source's own truncation habits; CSS
 *  `text-overflow: ellipsis` (`.tru`) still elides further on a narrower
 *  one. */
export function clampDetail(text: string, max = 72): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1).trimEnd()}…`;
}

/** One hue per source, matching the board's own picks (`pages_agora.py`'s
 *  `today()`: mail sky, calendar butter, chat mint) -- fixed, not hashed
 *  from the row's own text, because the point is telling the three sources
 *  apart at a glance, the same hue every time a mail row appears. */
export const SOURCE_HUE: Record<WaitingRow['source'], number> = {
  mail: 210,
  calendar: 42,
  chat: 152,
};

/** The action button's own icon -- a reply arrow for replying to a mail, an
 *  arrow forward for everything that just opens something. */
export function actionIcon(label: string): IconName {
  return label === 'Reply' ? 'reply' : 'arrow-right';
}

/** Urgent first, then newest -- the board's own rule (`pages_agora.py`'s
 *  `today()` lists its five rows in exactly this order). Ties keep their
 *  incoming order (`Array.prototype.sort` is stable), so combining sources
 *  in a fixed order gives a deterministic result even when two rows share
 *  an instant. */
export function orderWaitingRows(rows: readonly WaitingRow[]): WaitingRow[] {
  return [...rows].sort((a, b) => {
    if (a.urgent !== b.urgent) return a.urgent ? -1 : 1;
    return new Date(b.whenIso).getTime() - new Date(a.whenIso).getTime();
  });
}

const NO_REPLY_LOCAL =
  /^(no[-_.]?reply|do[-_.]?not[-_.]?reply|mailer-daemon|notifications?)(\+.*)?$/i;

/** `ThreadSummary` carries no headers, so List-Unsubscribe and List-Id cannot
 *  be read here (agora#473, agora#484 for @wtfalch/email); the sender's
 *  local part is all there is to judge by. The `(\+.*)?` tail matches
 *  plus-addressing (`no-reply+tag@`) without matching a local part that
 *  merely contains one of these words (`anoreply@`, `noreply.jones@`). */
function isNoReplySender(from: ThreadSummary['from']): boolean {
  const local = from?.email.trim().split('@')[0] ?? '';
  return NO_REPLY_LOCAL.test(local);
}

/** Unread threads in the inbox sent to `me` -- an unread thread a rule filed
 *  past the inbox, or one only cc'd to this address, is not "waiting for
 *  you" in the sense this card means. */
export function mailWaitingRows(
  threads: readonly ThreadSummary[],
  me: string,
  agoraId: string,
  inboxId: string,
): WaitingRow[] {
  const meLower = me.trim().toLowerCase();
  return threads
    .filter(
      (t) =>
        t.unreadCount > 0 &&
        t.to.some((a) => a.email.trim().toLowerCase() === meLower) &&
        !isNoReplySender(t.from),
    )
    .map((t) => ({
      id: `mail:${t.id}`,
      source: 'mail' as const,
      icon: 'mail' as const,
      who: t.from ? displayName(t.from) : '(no sender)',
      what: t.subject || '(no subject)',
      detail: clampDetail(t.preview),
      whenIso: t.receivedAt,
      urgent: false,
      action: {
        label: 'Reply',
        path: pathForMail(agoraId, { mailboxId: inboxId, threadId: t.id }),
      },
    }));
}

/** Whichever participant invited `me` -- the first owner or chair who is not
 *  `me`, falling back to the first other participant, and finally to
 *  nobody named at all (a scheduling tool with no human organizer, say). */
function organizerOf(event: CalendarEvent, meLower: string): CalendarParticipant | undefined {
  const others = (event.participants ?? []).filter(
    (p) => p.calendarAddress.trim().toLowerCase() !== meLower,
  );
  return others.find((p) => p.roles?.some((r) => r === 'owner' || r === 'chair')) ?? others[0];
}

function participantName(p: CalendarParticipant | undefined): string {
  if (!p) return 'Someone';
  return p.name?.trim() || p.calendarAddress;
}

/** Invitations this account has not answered: a participant row for `me`
 *  whose `participationStatus` is `needs-action`. */
export function calendarWaitingRows(
  events: readonly CalendarEvent[],
  me: string,
  agoraId: string,
): WaitingRow[] {
  const meLower = me.trim().toLowerCase();
  return events
    .filter((e) =>
      (e.participants ?? []).some(
        (p) =>
          p.calendarAddress.trim().toLowerCase() === meLower &&
          p.participationStatus === 'needs-action',
      ),
    )
    .map((e) => {
      const start = new Date(e.start);
      const end = new Date(start.getTime() + durationMinutes(e.duration) * 60_000);
      return {
        id: `calendar:${e.id}`,
        source: 'calendar' as const,
        icon: 'calendar' as const,
        who: participantName(organizerOf(e, meLower)),
        what: `invited you to ${e.title}`,
        detail: clampDetail(
          e.location ? `${eventTimeLabel(start, end)} · ${e.location}` : eventTimeLabel(start, end),
        ),
        whenIso: e.start,
        urgent: false,
        invite: { eventId: e.id, title: e.title },
        action: {
          label: 'Answer',
          path: pathForCalendar(agoraId, { date: dateParam(start), eventId: e.id }),
        },
      };
    });
}

/** The latest unread mention in one room's timeline, or `null` for a room
 *  with none -- `readUpToEventId` marks where reading stopped, so anything
 *  after it (or everything, with nothing read yet) is still unread.
 *  `events` is oldest first, matching `ChatClient.timeline`'s own order. */
export function chatMentionRow(
  room: ChatRoomSummary,
  events: readonly ChatTimelineEvent[],
  readUpToEventId: string | null,
  agoraId: string,
): WaitingRow | null {
  const readIndex = readUpToEventId ? events.findIndex((e) => e.id === readUpToEventId) : -1;
  const unread = readIndex === -1 ? events : events.slice(readIndex + 1);
  const mention = [...unread].reverse().find((e) => e.mentionsMe && !e.deleted);
  if (!mention) return null;
  return {
    id: `chat:${room.id}`,
    source: 'chat',
    icon: 'chat',
    who: mention.senderName,
    what: `mentioned you in ${room.name}`,
    detail: clampDetail(mention.body),
    whenIso: new Date(mention.ts).toISOString(),
    urgent: false,
    action: {
      label: 'Open',
      path: pathForChat(agoraId, { roomId: room.id, threadId: null, eventId: null }),
    },
  };
}

export function chatWaitingRows(
  entries: readonly {
    room: ChatRoomSummary;
    events: readonly ChatTimelineEvent[];
    readUpToEventId: string | null;
  }[],
  agoraId: string,
): WaitingRow[] {
  return entries
    .map((e) => chatMentionRow(e.room, e.events, e.readUpToEventId, agoraId))
    .filter((r): r is WaitingRow => r !== null);
}

/** `YYYY-MM-DD` in local time, the shape `CalendarLocation.date` wants. */
export function dateParam(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Local midnight today, through local midnight `daysAhead` days out -- the
 *  window `Today` scans for both today's agenda and any invitation waiting
 *  on an answer. */
export function todayWindowRange(now: Date, daysAhead = 30): { after: string; before: string } {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + daysAhead);
  return { after: start.toISOString(), before: end.toISOString() };
}

/** Whether an event's master start falls on `now`'s local calendar day. */
export function isToday(event: Pick<CalendarEvent, 'start'>, now: Date): boolean {
  const start = new Date(event.start);
  return (
    start.getFullYear() === now.getFullYear() &&
    start.getMonth() === now.getMonth() &&
    start.getDate() === now.getDate()
  );
}

/** One line of "Your day". */
export type AgendaRow = {
  id: string;
  at: Date;
  title: string;
  where?: string;
  /** The event's own page, what the row opens. */
  path: string;
  /** Under way right now. */
  now: boolean;
  ended: boolean;
  /** The event's calendar's colour, when it has one. */
  color?: string;
  /** Everyone else on the event, by name. */
  people: string[];
  /** The call page, set only while the event is under way and has a call of
   *  this agora's own. */
  joinPath?: string;
};

function underWay(event: CalendarEvent, start: Date, now: Date): boolean {
  return (
    start.getTime() <= now.getTime() &&
    now.getTime() < start.getTime() + durationMinutes(event.duration) * 60_000
  );
}

/** Today's events, earliest first, each with the path that opens it. Drawn
 *  by `Today` itself rather than the package's `Agenda`, whose rows cannot be
 *  opened. */
export function agendaRows(
  events: readonly CalendarEvent[],
  now: Date,
  agoraId: string,
  calendarColors: ReadonlyMap<string, string> = new Map(),
  me = '',
): AgendaRow[] {
  const meLower = me.trim().toLowerCase();
  return events
    .filter((e) => isToday(e, now))
    .map((e) => ({ event: e, start: new Date(e.start) }))
    .sort((a, b) => a.start.getTime() - b.start.getTime())
    .map(({ event, start }) => ({
      id: event.id,
      at: start,
      title: event.title,
      where: event.location,
      path: pathForCalendar(agoraId, { date: dateParam(start), eventId: event.id }),
      now: underWay(event, start, now),
      ended: start.getTime() + durationMinutes(event.duration) * 60_000 <= now.getTime(),
      color: calendarColors.get(event.calendarId) || undefined,
      people: (event.participants ?? [])
        .filter((p) => p.calendarAddress.trim().toLowerCase() !== meLower)
        .map(participantName),
      ...(underWay(event, start, now) && hasOwnEventCall(event, agoraId)
        ? { joinPath: eventCallPath(agoraId, event.uid) }
        : {}),
    }));
}

/** A mail or chat row's display time, on the same ladder every other list in
 *  Agora uses (`mail/format.ts`'s own `formatWhen`). Calendar rows show the
 *  event's own start time in `detail` already (`eventTimeLabel`), so this is
 *  for the row's right-hand timestamp only. */
export function whenLabel(row: Pick<WaitingRow, 'whenIso'>, now?: Date): string {
  return formatWhen(row.whenIso, now);
}

/* ------------------------------------------------------------- greeting */

/** Morning until noon, afternoon until 6pm, evening after -- the local
 *  hour, the way a person reading this would say it themselves. */
export function timeOfDay(now: Date): 'Morning' | 'Afternoon' | 'Evening' {
  const hour = now.getHours();
  if (hour < 12) return 'Morning';
  if (hour < 18) return 'Afternoon';
  return 'Evening';
}

/** The first name to greet by: the mail account's own display name, or the
 *  local part of its address when it has none -- `MailSession` carries no
 *  separate "first name" field, so this is the closest real one. An
 *  `accountName` that is itself an address (`@wtfalch/email/mailbox/fake`'s
 *  own session carries the address twice, once as `username` and once as
 *  the account's `name`) is not a name either, so this falls through the
 *  same way as an empty one rather than greeting "Ada@example.com". */
export function greetingName(accountName: string, username: string): string {
  const trimmed = accountName.trim();
  const first = trimmed.includes('@') ? '' : trimmed.split(/\s+/)[0];
  if (first) return first;
  // An address's local part is a handle, not a name, and `ada` in a greeting
  // reads as a typo. Capitalise its first letter; a person with a real name
  // on the account never gets here.
  const local = username.trim().split('@')[0] ?? '';
  return local ? local.charAt(0).toUpperCase() + local.slice(1) : 'there';
}

const SMALL_NUMBERS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
];

/** "Five things want you" / "One thing wants you" / "Nothing wants you
 *  right now" -- the board's own count sentence (`pages_agora.py`'s
 *  `"Five things want you"`), spelled out the way the canvas does for a
 *  small count and numeralled past ten. */
export function waitingSummary(count: number): string {
  if (count === 0) return 'Nothing wants you right now';
  const word = SMALL_NUMBERS[count] ?? String(count);
  const capitalised = word.charAt(0).toUpperCase() + word.slice(1);
  return `${capitalised} thing${count === 1 ? '' : 's'} want${count === 1 ? 's' : ''} you`;
}

/** "and the first meeting is in eighteen minutes" / "and there are no
 *  meetings today" / "and your meetings today are over" -- the next of today's events that has not finished yet,
 *  against the clock. Events already under way read as "under way" rather
 *  than a countdown that has already reached zero. */
export function nextMeetingSummary(now: Date, todaysEvents: readonly CalendarEvent[]): string {
  const spans = todaysEvents
    .map((e) => {
      const start = new Date(e.start);
      return { start, end: new Date(start.getTime() + durationMinutes(e.duration) * 60_000) };
    })
    .filter((span) => span.end.getTime() > now.getTime())
    .sort((a, b) => a.start.getTime() - b.start.getTime());
  const next = spans[0];
  if (!next) {
    return todaysEvents.length > 0
      ? 'and your meetings today are over'
      : 'and there are no meetings today';
  }
  if (next.start.getTime() <= now.getTime()) return 'and your next meeting is under way';
  const minutes = Math.round((next.start.getTime() - now.getTime()) / 60_000);
  if (minutes < 60)
    return `and the first meeting is in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.round(minutes / 60);
  return `and the first meeting is in ${hours} hour${hours === 1 ? '' : 's'}`;
}

export type Greeting = { heading: string; lead: string };

/** The board's own top line: "Morning, William." over one sentence
 *  summarising the day from this account's own data -- how many things
 *  want you, and when the next meeting is. */
export function greeting(
  now: Date,
  accountName: string,
  username: string,
  waitingCount: number,
  todaysEvents: readonly CalendarEvent[],
): Greeting {
  const name = greetingName(accountName, username);
  return {
    heading: `${timeOfDay(now)}, ${name}.`,
    lead: `${waitingSummary(waitingCount)}, ${nextMeetingSummary(now, todaysEvents)}.`,
  };
}

/* -------------------------------------------------- around right now */

export type AroundPerson = {
  userId: string;
  name: string;
  state: Exclude<ChatPresence, 'offline'>;
  /** The direct room the person was found in. */
  path: string;
};

const PRESENCE_ORDER: Record<Exclude<ChatPresence, 'offline'>, number> = {
  online: 0,
  busy: 1,
  away: 2,
};

/** People from the joined direct rooms whose presence says they are at their
 *  machine, those online first. Anyone reading `offline` is left out, which
 *  is also what a company with presence off gives for everyone. */
export function aroundRightNow(
  rooms: readonly ChatRoomSummary[],
  membersOf: (roomId: string) => readonly { userId: string; name: string }[],
  presenceOf: (userId: string) => ChatPresence,
  me: string,
  agoraId: string,
  limit = 6,
): AroundPerson[] {
  const seen = new Map<string, AroundPerson>();
  for (const room of rooms) {
    if (!room.isDirect || room.membership !== 'join') continue;
    for (const member of membersOf(room.id)) {
      if (member.userId === me || seen.has(member.userId)) continue;
      const state = presenceOf(member.userId);
      if (state === 'offline') continue;
      seen.set(member.userId, {
        userId: member.userId,
        name: member.name,
        state,
        path: pathForChat(agoraId, { roomId: room.id, threadId: null, eventId: null }),
      });
    }
  }
  return [...seen.values()]
    .sort((a, b) => PRESENCE_ORDER[a.state] - PRESENCE_ORDER[b.state])
    .slice(0, limit);
}

/** "Marcus Yuen, Ada Lovelace +2": the first two names, then how many more. */
export function peopleLine(people: readonly string[]): string {
  const shown = people.slice(0, 2).join(', ');
  return people.length > 2 ? `${shown} +${people.length - 2}` : shown;
}
